import { useState } from "react";
import { Link } from "react-router-dom";
import type { TaskTypeDTO } from "@csp/shared";
import { api } from "../../lib/api";
import { useLoad } from "../../lib/hooks";
import { fmtDateTime } from "../../lib/format";
import { DataTable, type Column } from "../../components/DataTable";
import { SubTabs, TYPE_TABS } from "../../components/SubTabs";
import { PageHeading } from "../../components/PageHeading";
import { Badge, MaturityBadge } from "../../components/Badges";
import { categoryLabel } from "../../components/CategoryPicker";

type Status = "draft" | "learning" | "enabled" | "disabled";
const statusText: Record<Status, string> = { draft: "草稿", learning: "探索中", enabled: "已启用", disabled: "已停用" };
function statusOf(t: TaskTypeDTO): Status {
  if (t.enabled) return "enabled";
  if (t.maturity === "L0") return "draft";
  if (t.learningMode && (t.maturity === "L1" || t.maturity === "L2")) return "learning";
  return "disabled";
}

export function TypesPage() {
  const types = useLoad(() => api.types(true), []);
  const [filter, setFilter] = useState<Status | "all">("all");
  const [keyword, setKeyword] = useState("");
  const all = [...(types.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const q = keyword.trim().toLocaleLowerCase();
  const rows = all.filter(t => (filter === "all" || statusOf(t) === filter) && `${t.name} ${t.slug} ${t.category} ${categoryLabel(t.category)}`.toLocaleLowerCase().includes(q));
  const columns: Column<TaskTypeDTO>[] = [
    { key: "name", label: "名称", render: t => <Link to={`/admin/types/${t.id}`} translate="no">{t.name}</Link> },
    { key: "category", label: "分类", render: t => categoryLabel(t.category) },
    { key: "status", label: "状态", render: t => <Badge tone={t.enabled ? "green" : "gray"}>{statusText[statusOf(t)]}</Badge> },
    { key: "maturity", label: "成熟度", render: t => <MaturityBadge maturity={t.maturity} /> },
    { key: "sop", label: "SOP 版本", render: t => `v${t.skillVersion}` },
    { key: "updated", label: "更新时间", render: t => fmtDateTime(t.updatedAt) },
    { key: "actions", label: "操作", render: t => <Link className="btn" to={`/admin/types/${t.id}`}>管理类型</Link> },
  ];
  return <div>
    <PageHeading title="任务类型" desc="管理已保存的类型、SOP 和探索记录；草稿始终保留在列表中">
      <Link className="btn btn-primary" to="/types/new">＋ 添加任务类型</Link>
    </PageHeading>
    <SubTabs tabs={TYPE_TABS} />
    <div className="card">
      <div className="row" style={{ flexWrap: "wrap", marginBottom: 16 }}>
        <input type="search" aria-label="搜索任务类型" placeholder="搜索名称、分类或英文标识" value={keyword} onChange={e => setKeyword(e.target.value)} />
        <button className={`btn${filter === "all" ? " btn-primary" : ""}`} onClick={() => setFilter("all")}>全部 ({all.length})</button>
        {(Object.keys(statusText) as Status[]).map(s => <button key={s} className={`btn${filter === s ? " btn-primary" : ""}`} aria-pressed={filter === s} onClick={() => setFilter(s)}>{statusText[s]} ({all.filter(t => statusOf(t) === s).length})</button>)}
        <button className="btn" onClick={() => void types.reload()}>刷新</button>
      </div>
      <p className="muted small">「探索中」表示未启用的 L1 / L2 探索类型；是否正在执行请查看任务运行记录。</p>
      {types.error ? <div className="notice err">{types.error}</div> : types.loading && !types.data ? <p>加载中…</p> : <DataTable columns={columns} rows={rows} rowKey={t => t.id} empty={all.length ? "没有匹配的任务类型" : "还没有任务类型，点右上角「添加任务类型」开始"} />}
    </div>
  </div>;
}
